import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import styled from 'styled-components';
import moment from 'moment';
import GenericExportCSVModal from '../../shared/GenericExportCSVModal';

const cookies = new Cookies();

const readCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i=0;i < ca.length;i++) {
    let c = ca[i];
    while (c.charAt(0)==' ') c = c.substring(1,c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
  }
  return null;
};

const authHeaders = () => {
  const token = JSON.parse(readCookie('access_token') || 'null');
  return {
    source: 'humblebee_admin',
    Authorization: `Bearer ${token}`,
  };
};

// --- Styled Components ---
const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: 100vh;
  margin-left: 15%;
  width: 85%;

  @media (max-width: 991px) {
    margin-left: 0;
    width: 100%;
    padding: 15px;
  }
`;

const MetaList = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #f1f2f6;

  &:last-child {
    border-bottom: none;
  }
`;

const MetaLabel = styled.span`
  font-weight: 700;
  color: #636e72;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MetaValue = styled.span`
  color: #2d3436;
  font-weight: 600;
  font-size: 14px;
  text-align: right;
`;

const BackLink = styled.span`
  cursor: pointer;
  color: #636e72;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  width: fit-content;
  transition: color 0.2s;

  &:hover {
    color: #ff8701;
  }
`;

const PageTitle = styled.h2`
  font-size: 26px;
  font-weight: 800;
  color: #2d3436;
  margin-bottom: 25px;
`;

const InfoCard = styled(Card)`
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  margin-bottom: 25px;
  background: #fff;
`;

const SectionHeader = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #2d3436;
  padding: 20px 20px 0 20px;
`;

const QuickLinkBtn = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: #fff;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  color: #2d3436;
  font-weight: 600;
  margin-bottom: 12px;
  text-decoration: none !important;
  transition: all 0.2s;

  &:hover {
    border-color: #ff8701;
    color: #ff8701;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(255, 135, 1, 0.08);
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 20px;
`;

const GalleryCard = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid #e1e8ed;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 15px rgba(0,0,0,0.08);
  }
`;

const CheckboxOverlay = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  cursor: pointer;
  border: 1px solid #e1e8ed;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.1);
    border-color: #ff8701;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  background: #f1f2f6;
`;

const GalleryContent = styled.div`
  padding: 15px;
`;

const MediaMeta = styled.div`
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
`;

const LocationDetail = styled.div`
  font-size: 12px;
  color: #2c3e50;
  margin-bottom: 10px;
  background: #f8f9fa;
  padding: 8px;
  border-radius: 6px;
  border-left: 3px solid #ff8701;
  text-align: left;
  line-height: 1.4;
  
  div {
    margin-bottom: 2px;
  }
`;

const MediaDesc = styled.p`
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
  margin-bottom: 12px;
  height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ProjectDetails = ({ match, history }) => {
  const { projectId } = match.params;

  const [project, setProject] = useState(null);
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [zipping, setZipping] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);

  // Detailed, per-field export of this single project's Project Parameters — every
  // field shown on this page, so the user can pick exactly which ones they want.
  const PROJECT_EXPORT_COLUMNS = [
    { label: 'Project ID', key: 'projectId', formatter: (row) => row.projectId || '-' },
    { label: 'Name', key: 'name', formatter: (row) => row.name || '-' },
    { label: 'Status', key: 'status', formatter: (row) => row.status || 'inactive' },
    { label: 'Targeted States', key: 'targetedStates', formatter: (row) => (row.targetedStates && row.targetedStates.length) ? row.targetedStates.join(', ') : '-' },
    { label: 'Targeted Districts', key: 'targetedDistricts', formatter: (row) => (row.targetedDistricts && row.targetedDistricts.length) ? row.targetedDistricts.join(', ') : '-' },
    { label: 'Start Date', key: 'startDate', formatter: (row) => row.startDate ? moment(row.startDate).format('DD-MM-YYYY') : '-' },
    { label: 'End Date', key: 'endDate', formatter: (row) => row.endDate ? moment(row.endDate).format('DD-MM-YYYY') : 'Ongoing' },
    { label: 'Funder', key: 'funder', formatter: (row) => row.funder || '-' },
    { label: 'Target Beekeepers', key: 'targetedBeekeepersCount', formatter: (row) => row.targetedBeekeepersCount ?? 0 },
    { label: 'Target Clusters', key: 'targetedClustersCount', formatter: (row) => row.targetedClustersCount ?? 0 },
  ];

  const toggleSelectImage = (url) => {
    setSelectedImages(prev => {
      if (prev.includes(url)) {
        return prev.filter(u => u !== url);
      } else {
        return [...prev, url];
      }
    });
  };

  const toggleSelectAll = () => {
    const allUrls = mediaList.flatMap(media => {
      return (media.photos && media.photos.length > 0) ? media.photos : [media.image_url];
    });

    if (selectedImages.length === allUrls.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(allUrls);
    }
  };

  const loadJSZip = () => {
    return new Promise((resolve, reject) => {
      if (window.JSZip) {
        resolve(window.JSZip);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      script.onload = () => resolve(window.JSZip);
      script.onerror = () => reject(new Error('Failed to load JSZip'));
      document.body.appendChild(script);
    });
  };

  const downloadAllAsZip = async (specificUrls = null) => {
    try {
      setZipping(true);
      const JSZip = await loadJSZip();
      const zip = new JSZip();
      const folder = zip.folder(`Project_${project?.projectId || 'Media'}_Images`);

      const allUrls = specificUrls || mediaList.flatMap(media => {
        return (media.photos && media.photos.length > 0) ? media.photos : [media.image_url];
      });

      if (allUrls.length === 0) {
        alert("No images to download.");
        return;
      }

      const downloadPromises = allUrls.map(async (url, idx) => {
        try {
          const proxyUrl = `${process.env.REACT_APP_API_APP_FIELD_URL}/projects/proxy-image?url=${encodeURIComponent(url)}`;
          const response = await fetch(proxyUrl, { headers: authHeaders() });
          if (!response.ok) throw new Error('Network error');
          const blob = await response.blob();
          const ext = url.split('.').pop().split('?')[0] || 'jpg';
          folder.file(`image_${idx + 1}.${ext}`, blob);
        } catch (err) {
          console.error(`Failed to fetch image: ${url}`, err);
        }
      });

      await Promise.all(downloadPromises);

      const content = await zip.generateAsync({ type: "blob" });
      const blobUrl = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Project_${project?.projectId || 'Media'}_Images.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Zipping error:", error);
      alert("Failed to create ZIP file.");
    } finally {
      setZipping(false);
    }
  };

  const downloadSingleImage = async (url, filename) => {
    try {
      const proxyUrl = `${process.env.REACT_APP_API_APP_FIELD_URL}/projects/proxy-image?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl, { headers: authHeaders() });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(url, '_blank');
    }
  };

  const exportProjectMediaCSV = () => {
    if (mediaList.length === 0) {
      alert("No project media available to export.");
      return;
    }

    const headers = ['S.No.', 'Date', 'Type', 'Block', 'District', 'State', 'Description', 'S3 Photo / Video URL'];
    const rows = [];

    let count = 1;
    mediaList.forEach(media => {
      const photos = (media.photos && media.photos.length > 0) ? media.photos : (media.image_url ? [media.image_url] : []);
      const videos = (media.videos && media.videos.length > 0) ? media.videos : (media.video_url ? [media.video_url] : []);
      const allUrls = [...photos, ...videos];

      allUrls.filter(Boolean).forEach(url => {
        rows.push([
          `"${count++}"`,
          `"${media.date ? moment(media.date).format('DD-MM-YYYY') : (media.created_at ? moment(media.created_at).format('DD-MM-YYYY') : '-')}"`,
          `"${media.media_type || (url.includes('.mp4') ? 'Video' : 'Photo')}"`,
          `"${media.block || media.location?.block || '-'}"`,
          `"${media.district || media.location?.district || '-'}"`,
          `"${media.state || media.location?.state || '-'}"`,
          `"${(media.description || 'No description').replace(/"/g, '""')}"`,
          `"${url.replace(/"/g, '""')}"`
        ].join(','));
      });
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${project?.name || 'Project'}_S3_Media_Links_${new Date().getTime()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchProjectData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_HUMBLEBEE_API_URL}/projects/${projectId}`,
        { headers: authHeaders() }
      );
      if (res.data && res.data.project) {
        setProject(res.data.project);
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectMedia = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_APP_FIELD_URL}/projects/${projectId}/media`,
        { headers: authHeaders() }
      );
      if (res.data && Array.isArray(res.data.media)) {
        setMediaList(res.data.media);
      }
    } catch (error) {
      console.error('Error fetching project media:', error);
    } finally {
      setMediaLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
      fetchProjectMedia();
    }
  }, [projectId]);

  if (loading) {
    return (
      <Container className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading Project Details...</p>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container className="text-center p-5">
        <h4>Project not found</h4>
        <BackLink onClick={() => history.push('/projects')}>
          <i className="fa fa-chevron-left"></i> Back to Projects
        </BackLink>
      </Container>
    );
  }

  return (
    <Container>
      <BackLink onClick={() => history.push('/projects')}>
        <i className="fa fa-chevron-left"></i> Back to Projects
      </BackLink>

      <PageTitle>{project.name}</PageTitle>

      <Row>
        {/* Left Side: Metadata Table */}
        <Col md={8}>
          <InfoCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 20 }}>
              <SectionHeader style={{ paddingBottom: 15 }}>Project Parameters</SectionHeader>
              <Button
                variant="outline-primary"
                size="sm"
                style={{ fontWeight: 'bold', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => setShowExportModal(true)}
              >
                <i className="fa fa-file-csv"></i>
                <span>Export CSV</span>
              </Button>
            </div>
            <Card.Body style={{ paddingTop: 0 }}>
              <MetaList>
                <MetaRow>
                  <MetaLabel><i className="fa fa-fingerprint text-primary" style={{ width: 16 }}></i> Project ID</MetaLabel>
                  <MetaValue style={{ fontFamily: 'monospace', fontWeight: 700 }}>{project.projectId || '-'}</MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaLabel><i className="fa fa-info-circle text-info" style={{ width: 16 }}></i> Status</MetaLabel>
                  <MetaValue>
                    <span 
                      className={`badge badge-${project.status === 'active' ? 'success' : 'secondary'}`} 
                      style={{ fontSize: '11px', padding: '5px 12px', borderRadius: '12px', textTransform: 'uppercase', fontWeight: 700 }}
                    >
                      {project.status || 'inactive'}
                    </span>
                  </MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaLabel><i className="fa fa-map-marker-alt text-danger" style={{ width: 16 }}></i> Targeted States</MetaLabel>
                  <MetaValue>{project.targetedStates?.join(', ') || '-'}</MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaLabel><i className="fa fa-map-signs text-warning" style={{ width: 16 }}></i> Targeted Districts</MetaLabel>
                  <MetaValue>{project.targetedDistricts?.join(', ') || '-'}</MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaLabel><i className="fa fa-calendar-alt text-success" style={{ width: 16 }}></i> Timeline</MetaLabel>
                  <MetaValue>
                    {project.startDate ? moment(project.startDate).format('DD MMM YYYY') : '-'} to {project.endDate ? moment(project.endDate).format('DD MMM YYYY') : 'Ongoing'}
                  </MetaValue>
                </MetaRow>
                {project.funder && (
                  <MetaRow>
                    <MetaLabel><i className="fa fa-hand-holding-usd text-success" style={{ width: 16 }}></i> Funder</MetaLabel>
                    <MetaValue>{project.funder}</MetaValue>
                  </MetaRow>
                )}
                <MetaRow>
                  <MetaLabel><i className="fa fa-users text-primary" style={{ width: 16 }}></i> Target Beekeepers</MetaLabel>
                  <MetaValue>{project.targetedBeekeepersCount?.toLocaleString('en-IN') || '0'} Beekeepers</MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaLabel><i className="fa fa-sitemap text-info" style={{ width: 16 }}></i> Target Clusters</MetaLabel>
                  <MetaValue>{project.targetedClustersCount || '0'} Clusters</MetaValue>
                </MetaRow>
              </MetaList>
            </Card.Body>
          </InfoCard>
        </Col>

        {/* Right Side: Quick Links */}
        <Col md={4}>
          <InfoCard>
            <SectionHeader style={{ paddingBottom: 15 }}>Quick Links</SectionHeader>
            <Card.Body style={{ paddingTop: 0 }}>
              <QuickLinkBtn to={`/clusters/${projectId}`}>
                <span>📁 Project Clusters</span>
                <i className="fa fa-chevron-right text-muted"></i>
              </QuickLinkBtn>

              <QuickLinkBtn to={`/fieldVisits/${projectId}`}>
                <span>🚶 Field Visits</span>
                <i className="fa fa-chevron-right text-muted"></i>
              </QuickLinkBtn>

              <QuickLinkBtn to={`/groupMeetings/${projectId}`}>
                <span>👥 Group Meetings</span>
                <i className="fa fa-chevron-right text-muted"></i>
              </QuickLinkBtn>

              <QuickLinkBtn to={`/training/${projectId}`}>
                <span>🎓 Trainings</span>
                <i className="fa fa-chevron-right text-muted"></i>
              </QuickLinkBtn>
            </Card.Body>
          </InfoCard>
        </Col>
      </Row>

      {/* Bottom Section: Media Gallery */}
      <InfoCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <SectionHeader style={{ paddingBottom: '20px' }}>Project Gallery (S3 Storage)</SectionHeader>
          {mediaList.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px' }}>
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ fontWeight: '600', borderColor: '#ccc' }}
                onClick={toggleSelectAll}
              >
                {selectedImages.length === mediaList.flatMap(m => (m.photos && m.photos.length > 0) ? m.photos : [m.image_url]).length ? (
                  <span>Deselect All</span>
                ) : (
                  <span>Select All</span>
                )}
              </button>

              <Button
                variant="outline-primary"
                size="sm"
                style={{
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onClick={exportProjectMediaCSV}
              >
                <i className="fa fa-file-csv"></i>
                <span>Export S3 Links CSV</span>
              </Button>

              {selectedImages.length > 0 ? (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={zipping}
                  style={{
                    backgroundColor: '#ff8701',
                    borderColor: '#ff8701',
                    color: '#fff',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onClick={() => downloadAllAsZip(selectedImages)}
                >
                  {zipping ? (
                    <>
                      <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px' }} />
                      <span>Zipping ({selectedImages.length})...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa fa-file-archive-o"></i>
                      <span>Download Selected ({selectedImages.length}) as ZIP</span>
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={zipping}
                  style={{
                    backgroundColor: '#ff8701',
                    borderColor: '#ff8701',
                    color: '#fff',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onClick={() => downloadAllAsZip(null)}
                >
                  {zipping ? (
                    <>
                      <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px' }} />
                      <span>Zipping...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa fa-file-archive-o"></i>
                      <span>Download All as ZIP</span>
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
        <Card.Body style={{ padding: 0 }}>
          {mediaLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" size="sm" />
              <p className="mt-2 text-muted">Loading Media...</p>
            </div>
          ) : mediaList.length === 0 ? (
            <div className="text-center p-5 text-muted">
              <i className="fa fa-images fa-3x mb-3 text-light-gray" style={{ color: '#b2bec3' }}></i>
              <p>No project-wise photos uploaded yet from the mobile app.</p>
            </div>
          ) : (
            <ImageGrid>
              {mediaList.flatMap((media) => {
                const photos = media.photos && media.photos.length > 0 ? media.photos : (media.image_url ? [media.image_url] : []);
                const videos = media.videos && media.videos.length > 0 ? media.videos : [];
                const desc = (media.description && media.description.toLowerCase() !== 'na' && media.description.toLowerCase() !== 'nan')
                  ? media.description
                  : 'No description provided.';
                  
                const photoCards = photos.map((url, idx) => (
                  <GalleryCard key={`${media._id}-photo-${idx}`}>
                    <CheckboxOverlay onClick={() => toggleSelectImage(url)}>
                      <input
                        type="checkbox"
                        checked={selectedImages.includes(url)}
                        onChange={() => {}}
                        style={{ cursor: 'pointer', transform: 'scale(1.2)', accentColor: '#ff8701' }}
                      />
                    </CheckboxOverlay>
                    <GalleryImage 
                      src={url} 
                      alt={desc} 
                      onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Image+Load+Error'; }}
                    />
                    <GalleryContent>
                      <MediaMeta>
                        <span>📅 {moment(media.date).format('DD-MM-YYYY')}</span>
                      </MediaMeta>
                      
                      <LocationDetail>
                        {media.village && <div><strong>Village:</strong> {media.village}</div>}
                        {media.block && <div><strong>Block:</strong> {media.block}</div>}
                        {media.district && <div><strong>District:</strong> {media.district}</div>}
                        {media.state && <div><strong>State:</strong> {media.state}</div>}
                        {media.lat && media.long && (
                          <div style={{ marginTop: '5px' }}>
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${media.lat},${media.long}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#ff8701', textDecoration: 'none', fontWeight: 'bold' }}
                            >
                              📍 Open GPS Location
                            </a>
                          </div>
                        )}
                      </LocationDetail>

                      <MediaDesc>{desc}</MediaDesc>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        style={{ width: '100%', borderColor: '#ff8701', color: '#ff8701' }}
                        onClick={() => downloadSingleImage(url, `project_image_${idx + 1}.jpg`)}
                      >
                        <i className="fa fa-download mr-1"></i> Download Original
                      </Button>
                    </GalleryContent>
                  </GalleryCard>
                ));

                const videoCards = videos.map((url, idx) => (
                  <GalleryCard key={`${media._id}-video-${idx}`}>
                    <video 
                      src={url}
                      controls
                      style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '8px 8px 0 0', backgroundColor: '#000' }}
                    >
                      Your browser does not support video play.
                    </video>
                    <GalleryContent>
                      <MediaMeta>
                        <span>🎥 Video • 📅 {moment(media.date).format('DD-MM-YYYY')}</span>
                      </MediaMeta>

                      <LocationDetail>
                        {media.village && <div><strong>Village:</strong> {media.village}</div>}
                        {media.block && <div><strong>Block:</strong> {media.block}</div>}
                        {media.district && <div><strong>District:</strong> {media.district}</div>}
                        {media.state && <div><strong>State:</strong> {media.state}</div>}
                      </LocationDetail>

                      <MediaDesc>{desc}</MediaDesc>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        style={{ width: '100%', borderColor: '#ff8701', color: '#ff8701' }}
                        onClick={() => window.open(url, '_blank')}
                      >
                        <i className="fa fa-external-link-alt mr-1"></i> Open / Download Video
                      </Button>
                    </GalleryContent>
                  </GalleryCard>
                ));

                return [...photoCards, ...videoCards];
              })}
            </ImageGrid>
          )}
        </Card.Body>
      </InfoCard>

      <GenericExportCSVModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        data={project ? [project] : []}
        columns={PROJECT_EXPORT_COLUMNS}
        title="Export Project Details to CSV"
        filenamePrefix={`project_${project?.projectId || project?.name || 'details'}`}
      />
    </Container>
  );
};

export default ProjectDetails;
